import { useRef } from 'preact/hooks';
import { ProgressBar } from 'components/ProgressBar';
import HelperText from 'components/HelperText';
import Link from 'components/Link';

import { Stack } from 'components/Stack';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FileName,
  FileNameContainer,
  FileExt,
  FileIcon,
  RemoveIcon,
  RemoveButton,
  DownloadIcon,
  ProgressWrapper,
  Content,
} from './Attachment.styles';
import { formatFileName } from './Attachment.utils';

interface AttachmentProps {
  fileName: string;
  downloadUrl?: string;
  isLoading?: boolean;
  error?: string;
  downloadable?: boolean;
  progress?: number;
  onRemove?: () => void;
}

export const Attachment = ({
  fileName,
  downloadUrl = '',
  downloadable = false,
  isLoading = false,
  error = '',
  progress = 0,
  onRemove,
}: AttachmentProps): JSX.Element => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [name, suffix] = formatFileName(fileName);

  return (
    <Stack gap={4} direction="column" isFullWidth>
      <Box loading={isLoading} error={!!error}>
        <Content ref={contentRef}>
          <FileIcon aria-hidden />
          <FileNameContainer id="download-attachment-name">
            <FileName>{name}</FileName>
            <FileExt>{suffix}</FileExt>
          </FileNameContainer>
          {onRemove && !isLoading && (
            <RemoveButton
              onClick={() => onRemove()}
              aria-label={`${t('ALT_TEXT_REMOVE_ICON')} ${fileName}`}
            >
              <RemoveIcon aria-hidden />
            </RemoveButton>
          )}
          {downloadable && downloadUrl && (
            <Link
              url={downloadUrl}
              type="url"
              ariaLabel={`${t(
                'ALT_TEXT_DOWNLOAD_ATTACHMENT_LINK',
              )} ${fileName}`}
            >
              <DownloadIcon aria-hidden />
            </Link>
          )}
        </Content>

        {isLoading && (
          <ProgressWrapper>
            <ProgressBar value={progress} />
          </ProgressWrapper>
        )}
      </Box>

      {error && (
        <HelperText isError size="sm">
          {error}
        </HelperText>
      )}
    </Stack>
  );
};
